/**
 * Mobile navigation for PLP V3/V4, selected by Header using the experiment value.
 * Keeps the original layout and callbacks while replacing the right-side icons with search,
 * the bottom-bar cart artwork and a shared animated wishlist link.
 */
import { useState } from 'react';
import { AppLink } from '@/shared/components/AppLink';
import { Menu, Search as SearchIcon } from 'lucide-react';
import HamburgerMenu from '@/shared/features/menu/presentation/components/HamburgerMenu';
import { Logo } from '@/shared/icons/logo';
import { WishlistFeedbackIcon } from '@/shared/widgets/WishlistFeedbackIcon';
import { useLocation } from 'react-router-dom';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';
import {
  HEADER_CTA_CLICKED,
  headerCtaClickedEventProperties,
} from '@/shared/features/analytics/utils/headerCtaClicked';

interface HeaderMobileProps {
  cartCount: number;
  wishlistCount: number;
  isLoggedIn: boolean;
  userName?: string;
  onSearch: (query: string) => void;
  onSearchIconClick: () => void;
  onOpenLogin?: (mode?: 'login' | 'signup') => void;
}

export default function HeaderMobile({ cartCount, wishlistCount, isLoggedIn, onSearch: _onSearch, onSearchIconClick, onOpenLogin }: HeaderMobileProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const isListingPage = /^\/(?:jewellery|search|plp)(?:[/.]|$)/.test(pathname)
    || /^\/recently-viewed\/?$/.test(pathname)
    || /^\/stores\/[^/]+\/catalog/.test(pathname);
  const { analytics, isInitialized } = useAnalytics();

  const handleSearchIconClick = () => {
    if (analytics && isInitialized) {
      try {
        analytics.trackEvent('search_icon_click', {
          location: 'header_mobile',
          action: 'open_search_modal'
        });
      } catch (error) {
        console.error('Error tracking search icon click:', error);
      }
    } else {
      console.warn('Analytics not initialized yet, event not tracked');
    }
    onSearchIconClick();
  };

  return (
    <>
      <div className="px-[15px]">
        <div className="flex items-center justify-between gap-3 py-3">
          {!isListingPage && (
            <button
              type="button"
              onClick={() => setIsMenuOpen(true)}
              className="flex items-center justify-center text-gray-700 hover:text-gray-900 transition-colors"
              aria-label="Menu"
            >
              <Menu className="h-6 w-6" />
            </button>
          )}

          {/* Center: Logo */}
          <div className="logo flex-1 flex justify-start">
            <AppLink
              to="/"
              eventName="BlueStone Logo Clicked"
              className="flex items-center hover:opacity-80 transition-opacity"
              aria-label="BlueStone Logo"
            >
              <Logo showLink={false} className="h-auto w-full max-w-[160px] min-w-[120px] text-gray-900" />
            </AppLink>
          </div>

          {/* Right: Icons */}
          <div className="flex items-center gap-4">
            {/* Retain the existing search handler and artwork; gray-600 lightens the icon. */}
            {/* Search Icon */}
            <button
              type="button"
              onClick={handleSearchIconClick}
              className="flex items-center justify-center text-gray-600 hover:text-gray-900 transition-colors"
              aria-label="Search"
            >
              <SearchIcon className="h-6 w-6" />
            </button>

            {/* Use the bottom-bar SVG at 28px; the /order destination and live cart badge remain intact. */}
            {/* Cart */}
            <AppLink
              to="/order"
              shouldRefresh
              eventName={HEADER_CTA_CLICKED}
              eventProperties={headerCtaClickedEventProperties('Cart')}
              className="relative flex items-center justify-center text-gray-700 hover:text-gray-900 transition-colors"
              aria-label="Cart"
            >
              <svg className="h-7 w-7 text-gray-600 group-hover:scale-110 transition-transform group-hover:text-hover" viewBox="0 0 25 25" fill="none" aria-hidden>
            <path d="M2.49805 3.21024C2.49805 3.02084 2.57329 2.83919 2.70722 2.70526C2.84114 2.57133 3.02279 2.49609 3.21219 2.49609H3.83779C4.87616 2.49609 5.47319 3.17168 5.81883 3.84583C6.0545 4.30717 6.22447 4.86992 6.36587 5.3584H21.0702C21.2909 5.35846 21.5086 5.40967 21.7061 5.508C21.9037 5.60633 22.0758 5.74911 22.2089 5.92514C22.3421 6.10116 22.4326 6.30564 22.4734 6.52253C22.5142 6.73942 22.5042 6.96282 22.4442 7.17519L20.3075 14.7094C20.1375 15.3072 19.7772 15.8332 19.2813 16.2077C18.7854 16.5823 18.1809 16.7848 17.5594 16.7848H10.3051C9.67848 16.785 9.06917 16.5791 8.57104 16.199C8.07292 15.8188 7.71362 15.2854 7.5485 14.6809L6.63153 11.3172L6.61725 11.2715L5.14611 6.27536L5.00328 5.79403C4.86045 5.29984 4.73476 4.86278 4.54622 4.49571C4.31912 4.05294 4.11059 3.92439 3.83779 3.92439H3.21219C3.02279 3.92439 2.84114 3.84915 2.70722 3.71522C2.57329 3.58129 2.49805 3.39965 2.49805 3.21024ZM7.99413 10.8859L8.92681 14.3081C9.0095 14.6102 9.1892 14.8768 9.43825 15.0667C9.6873 15.2567 9.99189 15.3595 10.3051 15.3593H17.5609C17.8714 15.359 18.1734 15.2575 18.4211 15.0701C18.6688 14.8827 18.8487 14.6197 18.9334 14.321L21.0702 6.78669H6.78722L7.99413 10.8859ZM12.5004 20.3541C12.5004 20.9223 12.2747 21.4672 11.8729 21.869C11.4711 22.2708 10.9262 22.4965 10.358 22.4965C9.78975 22.4965 9.24481 22.2708 8.84302 21.869C8.44124 21.4672 8.21552 20.9223 8.21552 20.3541C8.21552 19.7859 8.44124 19.2409 8.84302 18.8391C9.24481 18.4374 9.78975 18.2116 10.358 18.2116C10.9262 18.2116 11.4711 18.4374 11.8729 18.8391C12.2747 19.2409 12.5004 19.7859 12.5004 20.3541ZM11.0721 20.3541C11.0721 20.1647 10.9969 19.983 10.8629 19.8491C10.729 19.7152 10.5474 19.6399 10.358 19.6399C10.1686 19.6399 9.98691 19.7152 9.85298 19.8491C9.71905 19.983 9.64381 20.1647 9.64381 20.3541C9.64381 20.5435 9.71905 20.7251 9.85298 20.8591C9.98691 20.993 10.1686 21.0682 10.358 21.0682C10.5474 21.0682 10.729 20.993 10.8629 20.8591C10.9969 20.7251 11.0721 20.5435 11.0721 20.3541ZM19.6419 20.3541C19.6419 20.9223 19.4162 21.4672 19.0144 21.869C18.6126 22.2708 18.0676 22.4965 17.4994 22.4965C16.9312 22.4965 16.3863 22.2708 15.9845 21.869C15.5827 21.4672 15.357 20.9223 15.357 20.3541C15.357 19.7859 15.5827 19.2409 15.9845 18.8391C16.3863 18.4374 16.9312 18.2116 17.4994 18.2116C18.0676 18.2116 18.6126 18.4374 19.0144 18.8391C19.4162 19.2409 19.6419 19.7859 19.6419 20.3541ZM18.2136 20.3541C18.2136 20.1647 18.1383 19.983 18.0044 19.8491C17.8705 19.7152 17.6888 19.6399 17.4994 19.6399C17.31 19.6399 17.1284 19.7152 16.9945 19.8491C16.8605 19.983 16.7853 20.1647 16.7853 20.3541C16.7853 20.5435 16.8605 20.7251 16.9945 20.8591C17.1284 20.993 17.31 21.0682 17.4994 21.0682C17.6888 21.0682 17.8705 20.993 18.0044 20.8591C18.1383 20.7251 18.2136 20.5435 18.2136 20.3541Z" fill="currentColor" />
          </svg>
              {cartCount > 0 ? (
                <span className="absolute -top-2 -right-2 flex items-center justify-center min-w-[24px] h-5 px-1 rounded-md border border-gray-700 bg-white text-gray-700 text-xs font-semibold tabular-nums group-hover:border-hover group-hover:text-hover transition-colors">
                  {cartCount}
                </span>
              ) : null}
            </AppLink>

            {/* Shared count-driven feedback after a successful wishlist addition. */}
            <AppLink
              to="/wishlist/view"
              shouldRefresh
              eventName={HEADER_CTA_CLICKED}
              eventProperties={headerCtaClickedEventProperties('Wishlist')}
              className="flex items-center justify-center text-gray-1000"
              aria-label="Wishlist"
              onClick={(e) => {
                if (!isLoggedIn && onOpenLogin) {
                  e.preventDefault();
                  onOpenLogin('login');
                }
              }}
            >
              <WishlistFeedbackIcon count={wishlistCount} enabled={isLoggedIn && isListingPage} />
            </AppLink>
          </div>
        </div>
      </div>

      {!isListingPage && (
        <HamburgerMenu
          isOpen={isMenuOpen}
          onClose={() => setIsMenuOpen(false)}
          onOpenLogin={onOpenLogin}
        />
      )}
    </>
  );
}
